export const REDACTED = '<masked>';

/**
 * `q` is the only free-text URL param in the app. Every other filter param is a
 * validated enum, UUID, ISO date or number (packages/filters/src/state.ts), so
 * exactly one key needs redacting and there is no risk of scrubbing a useful value.
 *
 * Value matching rather than a key allowlist: the `$initial_` and
 * `$session_entry_` families, nested redirect URLs, array elements and prose
 * that embeds a link are all covered by looking for the parameter itself.
 *
 * `\s` stops the match at whitespace so a prose-embedded `?q=...` does not
 * consume trailing words. A hand-built URL-like string with a raw unencoded
 * literal-space multi-word `q` value is only masked through the first space
 * (e.g. `?q=jane doe` → `?q=<masked> doe`). Real browser-encoded URL props
 * use `%20`/`+` and are fully redacted.
 */
const SENSITIVE_QUERY_PATTERN = /([?&]q=)[^&#\s]*/g;

/** Keys parsed structurally by PostHog's ingestion; rewriting them breaks features. */
const NEVER_REWRITE: ReadonlySet<string> = new Set([
  // Removing this drops the whole event at ingestion.
  'token',
  // Ingestion does `new URL(key)` on heatmap keys and rejects invalid ones.
  '$heatmap_data',
  // Structural rrweb payload; rewriting breaks the replay player.
  '$snapshot_data',
  // Parsed by chainToElements server-side.
  '$elements_chain',
  // Keyed for source-map symbolication.
  '$exception_list',
]);

export function redactUrlString(input: string): string {
  return input.replace(SENSITIVE_QUERY_PATTERN, `$1${REDACTED}`);
}

function redactValue(value: unknown): unknown {
  if (typeof value === 'string') return redactUrlString(value);
  if (Array.isArray(value)) return value.map(redactValue);
  if (value && typeof value === 'object') return redactProperties(value as Record<string, unknown>);
  return value;
}

/**
 * Walks every leaf, arrays included. A key-based recursion that skips arrays is
 * exactly where an embedded `?q=` survives; masking is value-driven, so the only
 * keys that need special-casing are the structural ones PostHog parses.
 */
export function redactProperties(props: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    out[key] = NEVER_REWRITE.has(key) ? value : redactValue(value);
  }
  return out;
}

export interface RedactablePayload {
  properties?: Record<string, unknown>;
  $set?: Record<string, unknown>;
  $set_once?: Record<string, unknown>;
}

/**
 * Redacts every slot posthog-js can put a URL in. `properties` and the top-level
 * `$set`/`$set_once` are siblings, not nested, so redacting `properties` alone
 * leaves the person-property copies intact.
 */
export function redactCaptureResult<T extends RedactablePayload>(payload: T): T {
  const out = { ...payload };
  if (out.properties) out.properties = redactProperties(out.properties);
  if (out.$set) out.$set = redactProperties(out.$set);
  if (out.$set_once) out.$set_once = redactProperties(out.$set_once);
  return out;
}

/**
 * Returns the dotted path of every string still containing `needle`.
 *
 * For CI assertions and the manual redaction test, where the needle is a
 * specific value a tester searched for. Do NOT use this at runtime with the
 * app's own origin as the needle: `$current_url` legitimately contains the
 * origin on every single event, so it would report a leak every time.
 */
export function findUnredacted(payload: RedactablePayload, needle: string): string[] {
  if (!needle) return [];
  const hits: string[] = [];

  const walk = (value: unknown, path: string): void => {
    if (typeof value === 'string') {
      if (value.includes(needle)) hits.push(path);
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((item, index) => walk(item, `${path}[${index}]`));
      return;
    }
    if (value && typeof value === 'object') {
      for (const [key, child] of Object.entries(value)) {
        walk(child, path ? `${path}.${key}` : key);
      }
    }
  };

  walk(payload.properties, 'properties');
  walk(payload.$set, '$set');
  walk(payload.$set_once, '$set_once');
  return hits;
}
